import React, { useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormHelperText,
    CircularProgress,
    Typography,
    Alert,
    Input
} from "@mui/material";
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

// Asumo que este hook trae los Tipos de Comprobante (CO_TIPO_COMPROBANTE)
// Si no es así, deberás usar o crear un hook específico para CO_TIPO_COMPROBANTE
import { useComprobanteTypes } from "../../../hooks/accounting/useComprobanteTypes"; // Para TipComID


// Esquema de validación con Yup
const maintenanceDocSchema = yup.object().shape({
    // manid no se valida aquí, viene del contexto y no es editable
    docnom: yup.string().trim().required("El nombre del documento es obligatorio.").max(255, "El nombre no puede exceder 255 caracteres."),
    tipdoc: yup.string()
        .required("El tipo de documento es obligatorio.")
        .oneOf(['CONTABLE', 'FOTO', 'REPORTE', 'OTRO'], "Tipo de documento inválido."),
    tipcomid: yup.number()
        .nullable() // Puede ser nulo por defecto
        .when('tipdoc', { // Condicional: solo es requerido si tipdoc es 'CONTABLE'
            is: (tipdoc) => tipdoc === 'CONTABLE',
            then: (schema) => schema.required("El tipo de comprobante es obligatorio para documentos CONTABLES.").positive("Seleccione un tipo de comprobante válido."),
            otherwise: (schema) => schema.notRequired(), // No es requerido si no es CONTABLE
        }),
    // La validación del archivo se maneja manualmente ya que el input[type="file"]
    // no se gestiona directamente por Controller en React Hook Form.
});


const MaintenanceDocForm = ({ open, handleClose, onSubmit, defaultValues }) => {
    // Hook para obtener la lista de Tipos de Comprobante (para el selector de TipComID)
    const { comprobanteTypes: invoiceTypes, isLoading: isLoadingInvoiceTypes, error: errorInvoiceTypes } = useComprobanteTypes();

    const fileInputRef = useRef(null); // Ref para el input de archivo
    const [selectedFile, setSelectedFile] = useState(null);

    const {
        handleSubmit,
        control,
        watch, // Para observar cambios en tipdoc
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(maintenanceDocSchema),
        defaultValues: {
            manid: defaultValues.manid,
            docnom: defaultValues.docnom || "",
            tipdoc: defaultValues.tipdoc || "",
            tipcomid: defaultValues.tipcomid || null,
        },
    });

    const watchedTipDoc = watch("tipdoc");

    useEffect(() => {
        reset({
            manid: defaultValues.manid,
            docnom: defaultValues.docnom || "",
            tipdoc: defaultValues.tipdoc || "",
            tipcomid: defaultValues.tipcomid || null,
        });
        if (fileInputRef.current) {
            fileInputRef.current.value = ""; // Limpiar el input de archivo al resetear
        }
        setSelectedFile(null);
    }, [defaultValues, reset]);

    const handleFormSubmit = (data) => {
        if (!defaultValues.docid && !selectedFile) {
            alert("Debe seleccionar un archivo para subir.");
            return;
        }

        const payloadForParent = {
            ...data,
            manid: defaultValues.manid,
            selectedFile: selectedFile
        };
        onSubmit(payloadForParent);
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>{defaultValues.docid ? "Editar Documento de Mantenimiento" : "Añadir Documento de Mantenimiento"}</DialogTitle>
            <DialogContent dividers>
                <Box component="form" noValidate sx={{ mt: 1 }}>
                    {/* Información del Mantenimiento (solo lectura) */}
                    <TextField
                        margin="dense"
                        label="Mantenimiento"
                        value={defaultValues.mantenimientonombre || `Mantenimiento #${defaultValues.manid}`}
                        fullWidth
                        variant="outlined"
                        disabled
                        sx={{ mb: 2 }}
                    />

                    {/* Campo Nombre del Documento */}
                    <Controller
                        name="docnom"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                autoFocus
                                margin="dense"
                                label="Nombre del Documento"
                                type="text"
                                fullWidth
                                variant="outlined"
                                error={!!errors.docnom}
                                helperText={errors.docnom?.message}
                            />
                        )}
                    />

                    {/* Selector Tipo de Documento */}
                    <Controller
                        name="tipdoc"
                        control={control}
                        render={({ field }) => (
                            <FormControl fullWidth margin="dense" error={!!errors.tipdoc}>
                                <InputLabel id="tip-doc-label">Tipo de Documento</InputLabel>
                                <Select
                                    labelId="tip-doc-label"
                                    label="Tipo de Documento"
                                    {...field}
                                    value={field.value || ""} // Asegura que el valor sea "" para evitar warning si es null
                                >
                                    <MenuItem value=""><em>Seleccione</em></MenuItem>
                                    <MenuItem value="CONTABLE">CONTABLE</MenuItem>
                                    <MenuItem value="FOTO">FOTO</MenuItem>
                                    <MenuItem value="REPORTE">REPORTE</MenuItem>
                                    <MenuItem value="OTRO">OTRO</MenuItem>
                                </Select>
                                {errors.tipdoc && <FormHelperText>{errors.tipdoc.message}</FormHelperText>}
                            </FormControl>
                        )}
                    />

                    {/* Selector Tipo de Comprobante (Condicional) */}
                    {watchedTipDoc === 'CONTABLE' && (
                        isLoadingInvoiceTypes ? (
                            <CircularProgress size={24} sx={{ mb: 2 }} />
                        ) : errorInvoiceTypes ? (
                            <Alert severity="error" sx={{ mb: 2 }}>Error al cargar tipos de comprobante: {errorInvoiceTypes.message}</Alert>
                        ) : (
                            <Controller
                                name="tipcomid"
                                control={control}
                                render={({ field }) => (
                                    <FormControl fullWidth margin="dense" error={!!errors.tipcomid}>
                                        <InputLabel id="tip-com-id-label">Tipo de Comprobante</InputLabel>
                                        <Select
                                            labelId="tip-com-id-label"
                                            label="Tipo de Comprobante"
                                            {...field}
                                            value={field.value || ""}
                                        >
                                            <MenuItem value=""><em>Seleccione</em></MenuItem>
                                            {invoiceTypes.map((type) => (
                                                <MenuItem key={type.tipcomid} value={type.tipcomid}>
                                                    {type.tipcomnom} ({type.tipcomcod})
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {errors.tipcomid && <FormHelperText>{errors.tipcomid.message}</FormHelperText>}
                                    </FormControl>
                                )}
                            />
                        )
                    )}

                    {/* Input de Archivo */}
                    <Box mt={2} mb={2}>
                        <Typography variant="subtitle1" gutterBottom>Subir Archivo</Typography>

                        {/* Muestra el archivo actual si estamos editando y existe */}
                        {defaultValues.docid && defaultValues.docruta && (
                            <Typography variant="body2" color="text.secondary" mb={1}>
                                Archivo actual:
                                <a
                                    href={`${process.env.REACT_APP_API_URL}/documents/file/${defaultValues.docruta}`} // <-- CORRECCIÓN CRUCIAL AQUÍ
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ marginLeft: 4 }} // Pequeño estilo para separar el enlace
                                >
                                    {defaultValues.docnom}
                                </a>
                                <br />
                                Si subes uno nuevo, el anterior será reemplazado.
                            </Typography>
                        )}

                        {/* Ocultar el input nativo y usar un botón de Material-UI */}
                        <Button
                            variant="outlined"
                            component="label" // Permite que el botón actúe como un label para el input oculto
                            startIcon={<CloudUploadIcon />}
                            fullWidth
                            sx={{ mt: 1, mb: 1 }} // Margen para separar de otros elementos
                        >
                            {selectedFile ? selectedFile.name : (defaultValues.docid ? "Seleccionar nuevo archivo" : "Seleccionar archivo")}
                            <input
                                type="file"
                                hidden // Oculta el input nativo
                                ref={fileInputRef}
                                name="document"
                                onChange={(e) => {
                                    setSelectedFile(e.target.files[0] || null);
                                }}
                            />
                        </Button>

                        {!defaultValues.docid && !selectedFile && errors.root?.file && (
                            <FormHelperText error>{errors.root.file.message}</FormHelperText>
                        )}

                        {/*selectedFile && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                Archivo seleccionado: {selectedFile.name}
                            </Typography>
                        )*/}
                    </Box>

                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="inherit">
                    Cancelar
                </Button>
                <Button onClick={handleSubmit(handleFormSubmit)} variant="contained" color="primary">
                    {defaultValues.docid ? "Guardar Cambios" : "Subir Documento"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MaintenanceDocForm;